import { addCells } from "../addCells";
import { clearSelectedBorders } from "../clearSelectedBorders";
import { pushCellHistory } from "../pushCellHistory";
import { snapCells } from "../snapCells";
import { sortCells } from "../sortCells";
import { Cell, CELL_THRESHOLD, State } from "../types";

export type ClearMouseDragPositionAction = Readonly<{
  type: "CLEAR MOUSE DRAG POSITION";
  shiftKey: boolean;
}>;

function pointInsideRectangle(point: number[], topLeft: number[], bottomRight: number[]): boolean {
  return topLeft[0] <= point[0] && 
         point[0] <= bottomRight[0] && 
         topLeft[1] <= point[1] && point[1] <= bottomRight[1];
}

export const lineIntersectsRectangle = (lineStart: number[], lineEnd: number[], topLeft: number[], bottomRight: number[]): boolean => {
  // Check if either endpoint is inside the rectangle
  if (pointInsideRectangle(lineStart, topLeft, bottomRight) || pointInsideRectangle(lineEnd, topLeft, bottomRight)) {
      return true;
  }
  // Check if the line intersects any of the four sides of the rectangle
  if (
    // Intersect left wall  
    (lineStart[0] < topLeft[0] && lineEnd[0] > topLeft[0] && lineStart[1] < bottomRight[1] && lineStart[1] > topLeft[1]) ||
    // Intersect right wall
    (lineStart[0] < bottomRight[0] && lineEnd[0] > bottomRight[0] && lineStart[1] < bottomRight[1] && lineStart[1] > topLeft[1]) ||
    // Intersect top wall
    (lineStart[1] < topLeft[1] && lineEnd[1] > topLeft[1] && lineStart[0] < bottomRight[0] && lineStart[0] > topLeft[0]) ||
    // Intersect right wall
    (lineStart[1] < bottomRight[1] && lineEnd[1] > bottomRight[1] && lineStart[0] < bottomRight[0] && lineStart[0] > topLeft[0])    
  ) {
      return true;
  }

  return false;
}

export const clearMouseDragPosition = (
  state: State,
  _action: ClearMouseDragPositionAction,
): State => {
  let newCells: Array<Cell> = [];

  if (state.mouseDragStart === null || state.mousePosition === null) {
    return state;
  }

  // User attempting multi-select
  let draggingBorder = state.cells.find(x => x.selectedBorder);

  // To differentiate from single clicks
  let mouseDraggedDistance = (Math.abs(state.mouseDragStart[0]-state.mousePosition[0]) + 
                              Math.abs(state.mouseDragStart[1]-state.mousePosition[1]));

                              
  if (!draggingBorder && mouseDraggedDistance > 2) {
    const bottomRight = [
      Math.max(state.mouseDragStart[0], state.mousePosition[0]),
      Math.max(state.mouseDragStart[1], state.mousePosition[1]),
    ]
    const topLeft = [
      Math.min(state.mouseDragStart[0], state.mousePosition[0]),
      Math.min(state.mouseDragStart[1], state.mousePosition[1]),
    ]

    let newCells = state.cells.map(cell => {
      return  {
        ...cell,
        selected: false,
        selectedBorder: null
      }
    })
   
    // Select cells in the box
    newCells = newCells.map(cell => {
      if (cell.minX < bottomRight[0] && 
          cell.maxX > topLeft[0] &&
          cell.maxY > topLeft[1] &&
          cell.minY < bottomRight[1]) {
            return {
              ...cell,
              selected: true,
            }
          }
        return cell
    })
    

    const minX = newCells.reduce((prev, next) => {
      if (next.selectedBorder === "right") {
        return Math.max(next.minX, prev);
      }
      return prev;
    }, 0);
    const minY = newCells.reduce((prev, next) => {
      if (next.selectedBorder === "bottom") {
        return Math.max(next.minY, prev);
      }
      return prev;
    }, 0);
    const maxX = newCells.reduce((prev, next) => {
      if (next.selectedBorder === "left") {
        return Math.min(next.maxX, prev);
      }
      return prev;
    }, state.zoneWidth);
    const maxY = newCells.reduce((prev, next) => {
      if (next.selectedBorder === "top") {
        return Math.min(next.maxY, prev);
      }
      return prev;
    }, state.zoneHeight);

    return {
      ...state,
      cells: newCells,
      mouseDragStart: null,
      dragOffset: {
        maxX,
        maxY,
        minX,
        minY,
        x: 0,
        y: 0,
      },
    }
  } 

  // User dragging a border
  for (const cell of state.cells) {
    if (cell.selectedBorder === null) {
      continue;
    }
    if (cell.selectedBorder === "left" && state.dragOffset.x !== 0) {
      newCells.push({
        ...cell,
        minX: Math.max(
          state.dragOffset.minX,
          Math.min(state.dragOffset.maxX, cell.minX + state.dragOffset.x),
        ),
      });
    } else if (cell.selectedBorder === "right" && state.dragOffset.x !== 0) {
      newCells.push({
        ...cell,
        maxX: Math.max(
          state.dragOffset.minX,
          Math.min(state.dragOffset.maxX, cell.maxX + state.dragOffset.x),
        ),
      });
    } else if (cell.selectedBorder === "top" && state.dragOffset.y !== 0) {
      newCells.push({
        ...cell,
        minY: Math.max(
          state.dragOffset.minY,
          Math.min(state.dragOffset.maxY, cell.minY + state.dragOffset.y),
        ),
      });
    } else if (cell.selectedBorder === "bottom" && state.dragOffset.y !== 0) {
      newCells.push({
        ...cell,
        maxY: Math.max(
          state.dragOffset.minY,
          Math.min(state.dragOffset.maxY, cell.maxY + state.dragOffset.y),
        ),
      });
    }
  }
  const dragOffset = {
    ...state.dragOffset,
    x: 0,
    y: 0,
  };
  if (newCells.length < 1) {
    return {
      ...state,
      dragOffset,
      mouseDragStart: null,
    };
  }
  const cells = clearSelectedBorders(addCells(state.cells, newCells)) as Array<Cell>;
  return pushCellHistory(
    state,
    sortCells(snapCells(cells, state.zoneWidth, state.zoneHeight, CELL_THRESHOLD)),
    {
      dragOffset,
      mouseDragStart: null,
    },
  );
};
